import React, { useState } from 'react';
import { Menu, Popup } from 'semantic-ui-react';
import AgentLeftIcon from '../../icons/AgentLeftIcon';
import AgentRightIcon from '../../icons/AgentRightIcon';
import AgentPopoutIcon from '../../icons/AgentPopoutIcon';
import AgentPopinIcon from '../../icons/AgentPopinIcon';
import PanelButton from '../../components/PanelButton';
import './AgentDockModeDropdown.scss';

export type DockPosition = 'right' | 'left' | 'top' | 'bottom';
export type DockMode = 'inline' | 'overlay';

export interface AgentDockModeDropdownProps {
  dockPosition: DockPosition;
  dockMode: DockMode;
  onDockPositionChange: (position: DockPosition) => void;
  onDockModeChange: (mode: DockMode) => void;
}

interface DockOption {
  position: DockPosition;
  mode: DockMode;
  label: string;
  leftIcon: React.ReactElement;
  rightIcon: React.ReactElement;
}

export default function AgentDockModeDropdown({ dockPosition, dockMode, onDockPositionChange, onDockModeChange }: AgentDockModeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const dockOptions: DockOption[] = [
    {
      position: 'right',
      mode: 'inline',
      label: 'Right / Inline',
      leftIcon: <AgentRightIcon />,
      rightIcon: <AgentPopinIcon />
    },
    {
      position: 'right',
      mode: 'overlay',
      label: 'Right / Overlay',
      leftIcon: <AgentRightIcon />,
      rightIcon: <AgentPopoutIcon />
    },
    {
      position: 'left',
      mode: 'inline',
      label: 'Left / Inline',
      leftIcon: <AgentLeftIcon />,
      rightIcon: <AgentPopinIcon />
    },
    {
      position: 'left',
      mode: 'overlay',
      label: 'Left / Overlay',
      leftIcon: <AgentLeftIcon />,
      rightIcon: <AgentPopoutIcon />
    }
  ];

  const handleOptionClick = (option: DockOption) => {
    if (option.position !== dockPosition) {
      onDockPositionChange(option.position);
    }
    if (option.mode !== dockMode) {
      onDockModeChange(option.mode);
    }
    setIsOpen(false);
  };

  const handleTriggerClick = () => {
    setIsOpen(true);
  };

  const dropdownContent = (
    <Menu role="menu" vertical className="agent-dock-mode-dropdown-menu">
      {dockOptions.map((option) => (
        <Menu.Item
          key={`${option.position}-${option.mode}`}
          className={`agent-dock-mode-option ${option.position === dockPosition && option.mode === dockMode ? 'active' : ''}`}
          onClick={() => handleOptionClick(option)}
          role="menuitem"
        >
          <div className="agent-dock-mode-option-content">
            <div className="agent-dock-mode-left-icon">{option.leftIcon}</div>
            <span className="agent-dock-mode-label">{option.label}</span>
            <div className="agent-dock-mode-right-icon">{option.rightIcon}</div>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Popup
      open={isOpen}
      onClose={() => setIsOpen(false)}
      on="click"
      className="agent-dock-mode-dropdown"
      trigger={
        <PanelButton onClick={handleTriggerClick} aria-label="Change dock position and mode">
          <span className="ai-popup-header-icon">{dockPosition === 'left' ? <AgentLeftIcon /> : <AgentRightIcon />}</span>
        </PanelButton>
      }
      position="bottom left"
      content={dropdownContent}
    />
  );
}
